import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trash2, Play, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

interface SavedGame {
  id: string;
  opponent: string;
  result: 'win' | 'loss' | 'draw';
  date: string;
  moves: string[];
  gameState: any;
  duration?: number;
  userColor: 'white' | 'black';
}

interface SavedGamesListProps {
  onLoadGame: (game: SavedGame) => void;
}

export function SavedGamesList({ onLoadGame }: SavedGamesListProps) {
  const [savedGames, setSavedGames] = useState<SavedGame[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSavedGames();
  }, []);

  const loadSavedGames = () => {
    try {
      const games = localStorage.getItem('chesshatch_games');
      if (games) {
        const parsedGames = JSON.parse(games);
        setSavedGames(parsedGames.sort((a: SavedGame, b: SavedGame) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        ));
      }
    } catch (error) {
      console.error('Error loading saved games:', error);
      toast.error('Failed to load saved games');
    }
  };

  const deleteGame = (gameId: string) => {
    try {
      const updatedGames = savedGames.filter(game => game.id !== gameId);
      setSavedGames(updatedGames);
      localStorage.setItem('chesshatch_games', JSON.stringify(updatedGames));
      toast.success('Game deleted');
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error('Failed to delete game');
    }
  };

  const clearAllGames = () => {
    try {
      localStorage.removeItem('chesshatch_games');
      setSavedGames([]);
      toast.success('All games cleared');
    } catch (error) {
      console.error('Error clearing games:', error);
      toast.error('Failed to clear games');
    }
  };

  const filteredGames = savedGames.filter(game =>
    game.opponent.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getResultBadgeVariant = (result: string) => {
    switch (result) {
      case 'win': return 'default';
      case 'loss': return 'destructive';
      case 'draw': return 'secondary';
      default: return 'outline';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Saved Games ({savedGames.length})</CardTitle>
          {savedGames.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllGames}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
        
        {savedGames.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by opponent name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
      </CardHeader>

      <CardContent>
        {savedGames.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <p>No saved games found.</p>
            <p className="text-sm mt-1">Complete a game to see it here for analysis.</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">vs {game.opponent}</span>
                      <Badge variant={getResultBadgeVariant(game.result)} className="capitalize">
                        {game.result}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {game.userColor}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-muted-foreground">
                      <span>{formatDate(game.date)}</span>
                      <span className="mx-2">•</span>
                      <span>{game.moves.length} moves</span>
                      {game.duration && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{Math.floor(game.duration / 60000)}m</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onLoadGame(game)}
                      className="shrink-0"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Analyze
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteGame(game.id)}
                      className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}